/**
 * Brand voice helpers (#242).
 *
 * The styleguide's Voice section is the source of truth: progress is *narrated, not
 * measured* ("Three hundred and twenty-two stitches done, eight thousand and seventy-three
 * to go" — never "0% complete"), counts are spelled out in prose in narrative contexts,
 * and reward moments land with warmth. This module is the single home for that logic so
 * the wording can't drift between surfaces (the empty states, the resume prompts, the
 * progress panel). Tight UI — chips, the grid, axis labels — keeps digits; that's the
 * caller's call, this module just supplies the prose.
 *
 * Why a util and not inline strings: spelling numbers out correctly (British "and"
 * placement: "eight thousand and seventy-three", "one thousand one hundred") is fiddly
 * enough to be worth pinning with tests, and the owner is colourblind — they can't
 * eyeball a misframed reward, so the framing is decided in code and verified.
 */

// Encouragement shown in empty states and resume prompts. Lifted verbatim from the
// styleguide Voice specimen so the app and the guide stay in lockstep.
export const ENCOURAGEMENT = 'Take heart, and stitch.'
// The courtly charge — taglines and first-run invitations.
export const CHARGE = 'Take up the needle.'

const ONES = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
]
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']
// Indexed by group position (0 = units group, 1 = thousands, …). Covers stitch counts
// far beyond any real pattern; a chart with billions of stitches is not our problem.
const SCALES = ['', ' thousand', ' million', ' billion']

function tensToWords(n: number): string {
  if (n < 20) return ONES[n]
  const tens = TENS[Math.floor(n / 10)]
  const ones = n % 10
  return ones ? `${tens}-${ONES[ones]}` : tens
}

// 0..999 → prose ('' for 0). British "and": "one hundred and one", "three hundred and four".
function under1000ToWords(n: number): string {
  if (n === 0) return ''
  const hundreds = Math.floor(n / 100)
  const rest = n % 100
  if (hundreds === 0) return tensToWords(rest)
  const head = `${ONES[hundreds]} hundred`
  return rest ? `${head} and ${tensToWords(rest)}` : head
}

/**
 * Spell a non-negative integer out in British prose: 322 → "three hundred and
 * twenty-two", 8073 → "eight thousand and seventy-three", 1100 → "one thousand one
 * hundred". Input is floored; anything non-finite, negative, or flooring to zero yields
 * "zero" (callers pass counts, which can't legitimately be negative — we'd rather
 * degrade to "zero" than emit junk).
 */
export function numberToWords(value: number): string {
  // Floor first so a fractional value in (0,1) degrades to "zero", not "" — the
  // documented contract is "non-negative integer", and we round toward it.
  if (!Number.isFinite(value)) return 'zero'
  let n = Math.floor(value)
  if (n <= 0) return 'zero'

  // Split into thousand-groups, lowest first.
  const groups: number[] = []
  while (n > 0) {
    groups.push(n % 1000)
    n = Math.floor(n / 1000)
  }

  const segments: string[] = []
  // Higher groups (thousands and up), highest first.
  for (let i = groups.length - 1; i >= 1; i--) {
    if (groups[i] === 0) continue
    segments.push(under1000ToWords(groups[i]) + SCALES[i])
  }
  // The units group carries a leading "and" only when it's a bare 1–99 remainder behind
  // a larger part ("one thousand and one", "eight thousand and seventy-three"); a group
  // with hundreds reads without it ("one thousand one hundred").
  const units = groups[0]
  if (units > 0) {
    const unitWords = under1000ToWords(units)
    segments.push(segments.length > 0 && units < 100 ? `and ${unitWords}` : unitWords)
  }

  return segments.join(' ')
}

function stitchNoun(n: number): string {
  return n === 1 ? 'stitch' : 'stitches'
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * Narrate stitching progress as a quiet update rather than a dashboard metric — the
 * "progress narrated, not measured" principle. Counts are spelled out (the prose form),
 * not shown as a percent. Three shapes:
 *   - nothing done yet → an invitation, not "0% complete"
 *   - finished        → a warm reward framed from the count, not "100%"
 *   - in between       → "<done> stitches done, <remaining> to go"
 *
 * `total` is the pattern's stitch count (always > 0 for a real pattern); a non-positive
 * total yields an empty string so callers can hide the line rather than narrate nonsense.
 */
export function narrateProgress(completed: number, total: number): string {
  if (!Number.isFinite(total) || total <= 0) return ''
  const done = Math.max(0, Math.min(Math.floor(completed), total))
  const remaining = total - done

  if (done === 0) {
    return `${capitalize(numberToWords(total))} ${stitchNoun(total)} ahead — ${ENCOURAGEMENT}`
  }
  if (remaining === 0) {
    // The reward moment: built from a count we can actually see, framed with warmth.
    return 'Every stitch is in — the whole piece finished.'
  }
  return `${capitalize(numberToWords(done))} ${stitchNoun(done)} done, ${numberToWords(remaining)} to go.`
}
